import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Save, Plus, Trash2, Upload, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSiteSettings = () => {
  const [activeTab, setActiveTab] = useState('header');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // Header Settings
  const [headerSettings, setHeaderSettings] = useState({
    logo: {
      text: 'Cep Premium',
      image: null // Logo görseli için
    },
    announcementBar: {
      enabled: true,
      items: [
        { text: '🇹🇷 Türkiye Garantili', enabled: true },
        { text: '🔒 Güvenli Ödeme', enabled: true },
        { text: '🚚 Ücretsiz Kargo', enabled: true },
        { text: '⚡ Lansmana Özel Fırsatlar', enabled: true }
      ]
    },
    navigation: [
      { name: 'AI Çeviri Ürünleri', path: '/category/ai-ceviri', enabled: true, hasDropdown: false, submenu: [] },
      { name: 'Powerbank', path: '/category/powerbank', enabled: true, hasDropdown: false, submenu: [] },
      { name: 'Hoparlörler', path: '/category/speakers', enabled: true, hasDropdown: false, submenu: [] },
      { 
        name: 'Kulaklıklar', 
        path: '/category/headphones', 
        enabled: true, 
        hasDropdown: true,
        submenu: [
          { name: 'Bluetooth Kulaklıklar', path: '/category/bluetooth-headphones', enabled: true },
          { name: 'Kablolu Kulaklıklar', path: '/category/wired-headphones', enabled: true }
        ]
      },
      { name: 'Çantalar', path: '/category/bags', enabled: true, hasDropdown: false, submenu: [] },
      { name: 'iPhone 17 Serisi Kılıfları', path: '/category/iphone-17-cases', enabled: true, hasDropdown: false, submenu: [] },
      { name: 'Fanlar', path: '/category/fans', enabled: true, hasDropdown: false, submenu: [] },
      { 
        name: 'Araç Aksesuarları', 
        path: '/category/car', 
        enabled: true, 
        hasDropdown: true,
        submenu: [
          { name: 'Araç Kameraları', path: '/category/car-cameras', enabled: true },
          { name: 'Araç Tutucuları', path: '/category/car-holders', enabled: true }
        ]
      },
      { name: 'Şarj & Data Kabloları', path: '/category/cables', enabled: true, hasDropdown: false, submenu: [] },
      { name: 'Tüm Ürünler', path: '/products', enabled: true, hasDropdown: false, submenu: [] }
    ]
  });

  // Footer Settings
  const [footerSettings, setFooterSettings] = useState({
    description: 'Bluetooth kulaklıklar, powerbank, kablolar, yaka mikrofonları, tripodlar ve daha fazlası Cep Premium\'da! Kaliteli aksesuarları şimdi keşfet!',
    contact: {
      address: 'İstanbul, Türkiye',
      phone: '+90 551 834 88 83',
      email: 'info@ceppremium.com'
    },
    quickLinks: [
      { name: 'Hakkımızda', path: '/about', enabled: true },
      { name: 'İletişim', path: '/contact', enabled: true },
      { name: 'Kargo ve Teslimat', path: '/shipping', enabled: true },
      { name: 'İptal ve İade', path: '/returns', enabled: true },
      { name: 'Gizlilik Politikası', path: '/privacy', enabled: true }
    ],
    categories: [
      { name: 'Kulaklıklar', path: '/category/headphones', enabled: true },
      { name: 'Powerbank', path: '/category/powerbank', enabled: true },
      { name: 'Hoparlörler', path: '/category/speakers', enabled: true },
      { name: 'Kablolar', path: '/category/cables', enabled: true }
    ],
    socialMedia: {
      facebook: 'https://facebook.com/ceppremium',
      instagram: 'https://instagram.com/ceppremium',
      twitter: 'https://twitter.com/ceppremium',
      youtube: 'https://youtube.com/ceppremium'
    },
    customSections: []
  });

  // Load settings from API on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings`);
        const data = await response.json();
        
        if (data.headerSettings) {
          setHeaderSettings(data.headerSettings);
        }
        if (data.footerSettings) {
          setFooterSettings(data.footerSettings);
        }
      } catch (error) {
        console.error('Ayarlar yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          headerSettings,
          footerSettings
        })
      });

      if (response.ok) {
        setSaved(true);
        alert('✅ Ayarlar başarıyla kaydedildi!');
        setTimeout(() => setSaved(false), 3000);
      } else {
        throw new Error('Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      alert('❌ Ayarlar kaydedilirken bir hata oluştu!');
    }
  };

  // Logo image upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeaderSettings({
          ...headerSettings,
          logo: { ...headerSettings.logo, image: reader.result }
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Announcement bar functions
  const addAnnouncementItem = () => {
    setHeaderSettings({
      ...headerSettings,
      announcementBar: {
        ...headerSettings.announcementBar,
        items: [...headerSettings.announcementBar.items, { text: 'Yeni Duyuru', enabled: true }]
      }
    });
  };

  const removeAnnouncementItem = (index) => {
    setHeaderSettings({
      ...headerSettings,
      announcementBar: {
        ...headerSettings.announcementBar,
        items: headerSettings.announcementBar.items.filter((_, i) => i !== index)
      }
    });
  };

  const updateAnnouncementItem = (index, field, value) => {
    const updated = [...headerSettings.announcementBar.items];
    updated[index][field] = value;
    setHeaderSettings({
      ...headerSettings,
      announcementBar: { ...headerSettings.announcementBar, items: updated }
    });
  };

  // Navigation functions
  const addNavigationItem = () => {
    setHeaderSettings({
      ...headerSettings,
      navigation: [
        ...headerSettings.navigation,
        { name: 'Yeni Menü', path: '/yeni-menu', enabled: true, hasDropdown: false, submenu: [] }
      ]
    });
  };

  const removeNavigationItem = (index) => {
    setHeaderSettings({
      ...headerSettings,
      navigation: headerSettings.navigation.filter((_, i) => i !== index)
    });
  };

  const updateNavigationItem = (index, field, value) => {
    const updated = [...headerSettings.navigation];
    updated[index][field] = value;
    setHeaderSettings({ ...headerSettings, navigation: updated });
  };

  const addSubmenuItem = (navIndex) => {
    const updated = [...headerSettings.navigation];
    updated[navIndex].submenu.push({ name: 'Alt Menü', path: '/alt-menu', enabled: true });
    setHeaderSettings({ ...headerSettings, navigation: updated });
  };

  const removeSubmenuItem = (navIndex, subIndex) => {
    const updated = [...headerSettings.navigation];
    updated[navIndex].submenu = updated[navIndex].submenu.filter((_, i) => i !== subIndex);
    setHeaderSettings({ ...headerSettings, navigation: updated });
  };

  const updateSubmenuItem = (navIndex, subIndex, field, value) => {
    const updated = [...headerSettings.navigation];
    updated[navIndex].submenu[subIndex][field] = value;
    setHeaderSettings({ ...headerSettings, navigation: updated });
  };

  // Footer functions
  const addFooterLink = (type) => {
    const newLink = { name: 'Yeni Link', path: '/yeni-link', enabled: true };
    if (type === 'quick') {
      setFooterSettings({
        ...footerSettings,
        quickLinks: [...footerSettings.quickLinks, newLink]
      });
    } else {
      setFooterSettings({
        ...footerSettings,
        categories: [...footerSettings.categories, newLink]
      });
    }
  };

  const removeFooterLink = (type, index) => {
    if (type === 'quick') {
      setFooterSettings({
        ...footerSettings,
        quickLinks: footerSettings.quickLinks.filter((_, i) => i !== index)
      });
    } else {
      setFooterSettings({
        ...footerSettings,
        categories: footerSettings.categories.filter((_, i) => i !== index)
      });
    }
  };

  const updateFooterLink = (type, index, field, value) => {
    if (type === 'quick') {
      const updated = [...footerSettings.quickLinks];
      updated[index][field] = value;
      setFooterSettings({ ...footerSettings, quickLinks: updated });
    } else {
      const updated = [...footerSettings.categories];
      updated[index][field] = value;
      setFooterSettings({ ...footerSettings, categories: updated });
    }
  };

  // Custom sections functions
  const addCustomSection = () => {
    setFooterSettings({
      ...footerSettings,
      customSections: [
        ...footerSettings.customSections,
        { title: 'Yeni Bölüm', enabled: true, links: [] }
      ]
    });
  };

  const removeCustomSection = (index) => {
    setFooterSettings({
      ...footerSettings,
      customSections: footerSettings.customSections.filter((_, i) => i !== index)
    });
  };

  const updateCustomSection = (index, field, value) => {
    const updated = [...footerSettings.customSections];
    updated[index][field] = value;
    setFooterSettings({ ...footerSettings, customSections: updated });
  };

  const addCustomSectionLink = (sectionIndex) => {
    const updated = [...footerSettings.customSections];
    updated[sectionIndex].links.push({ name: 'Yeni Link', path: '/yeni-link', enabled: true });
    setFooterSettings({ ...footerSettings, customSections: updated });
  };

  const removeCustomSectionLink = (sectionIndex, linkIndex) => {
    const updated = [...footerSettings.customSections];
    updated[sectionIndex].links = updated[sectionIndex].links.filter((_, i) => i !== linkIndex);
    setFooterSettings({ ...footerSettings, customSections: updated });
  };

  const updateCustomSectionLink = (sectionIndex, linkIndex, field, value) => {
    const updated = [...footerSettings.customSections];
    updated[sectionIndex].links[linkIndex][field] = value;
    setFooterSettings({ ...footerSettings, customSections: updated });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Yükleniyor...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Site Ayarları</h1>
            <p className="text-gray-600 mt-2">Header ve Footer ayarlarını yönetin</p>
          </div>
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
              saved 
                ? 'bg-green-600 text-white' 
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            <Save size={20} />
            {saved ? 'Kaydedildi!' : 'Kaydet'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b">
          <button
            onClick={() => setActiveTab('header')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'header'
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-gray-600 hover:text-red-600'
            }`}
          >
            Header Ayarları
          </button>
          <button
            onClick={() => setActiveTab('footer')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'footer'
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-gray-600 hover:text-red-600'
            }`}
          >
            Footer Ayarları
          </button>
        </div>

        {/* Header Settings */}
        {activeTab === 'header' && (
          <div className="space-y-6">
            {/* Logo */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4">Logo</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Logo Metni</label>
                  <input
                    type="text"
                    value={headerSettings.logo.text}
                    onChange={(e) => setHeaderSettings({ 
                      ...headerSettings, 
                      logo: { ...headerSettings.logo, text: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Logo Görseli (İsteğe Bağlı)</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      <Upload size={18} />
                      Görsel Yükle
                    </label>
                    {headerSettings.logo.image && (
                      <div className="flex items-center gap-2">
                        <img src={headerSettings.logo.image} alt="Logo" className="h-12 object-contain" />
                        <button
                          onClick={() => setHeaderSettings({
                            ...headerSettings,
                            logo: { ...headerSettings.logo, image: null }
                          })}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Logo görseli yoksa metin kullanılır</p>
                </div>
              </div>
            </div>

            {/* Announcement Bar */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Üst Duyuru Barı</h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={addAnnouncementItem}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
                  >
                    <Plus size={16} />
                    Yeni Ekle
                  </button>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={headerSettings.announcementBar.enabled}
                      onChange={(e) => setHeaderSettings({
                        ...headerSettings,
                        announcementBar: { ...headerSettings.announcementBar, enabled: e.target.checked }
                      })}
                      className="w-4 h-4 accent-red-600"
                    />
                    <span className="text-sm font-medium">Aktif</span>
                  </label>
                </div>
              </div>
              <div className="space-y-3">
                {headerSettings.announcementBar.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={(e) => updateAnnouncementItem(index, 'enabled', e.target.checked)}
                      className="w-4 h-4 accent-red-600"
                    />
                    <input
                      type="text"
                      value={item.text}
                      onChange={(e) => updateAnnouncementItem(index, 'text', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      placeholder="Duyuru metni"
                    />
                    <button
                      onClick={() => removeAnnouncementItem(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Menu */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Navigasyon Menüsü</h2>
                <button
                  onClick={addNavigationItem}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
                >
                  <Plus size={16} />
                  Yeni Menü Ekle
                </button>
              </div>
              <div className="space-y-4">
                {headerSettings.navigation.map((item, navIndex) => (
                  <div key={navIndex} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="checkbox"
                        checked={item.enabled}
                        onChange={(e) => updateNavigationItem(navIndex, 'enabled', e.target.checked)}
                        className="w-4 h-4 accent-red-600"
                      />
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateNavigationItem(navIndex, 'name', e.target.value)}
                        placeholder="Menü Adı"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      />
                      <input
                        type="text"
                        value={item.path}
                        onChange={(e) => updateNavigationItem(navIndex, 'path', e.target.value)}
                        placeholder="/path"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      />
                      <label className="flex items-center gap-2 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={item.hasDropdown}
                          onChange={(e) => updateNavigationItem(navIndex, 'hasDropdown', e.target.checked)}
                          className="w-4 h-4 accent-red-600"
                        />
                        <span className="text-sm">Alt Menü</span>
                      </label>
                      <button
                        onClick={() => removeNavigationItem(navIndex)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>

                    {/* Submenu */}
                    {item.hasDropdown && (
                      <div className="ml-8 mt-3 space-y-2 border-l-2 border-red-600 pl-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Alt Menü Öğeleri</span>
                          <button
                            onClick={() => addSubmenuItem(navIndex)}
                            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-xs font-semibold hover:bg-blue-700"
                          >
                            <Plus size={14} />
                            Alt Menü Ekle
                          </button>
                        </div>
                        {item.submenu.map((subItem, subIndex) => (
                          <div key={subIndex} className="flex items-center gap-2 bg-white p-2 rounded">
                            <input
                              type="checkbox"
                              checked={subItem.enabled}
                              onChange={(e) => updateSubmenuItem(navIndex, subIndex, 'enabled', e.target.checked)}
                              className="w-3 h-3 accent-red-600"
                            />
                            <input
                              type="text"
                              value={subItem.name}
                              onChange={(e) => updateSubmenuItem(navIndex, subIndex, 'name', e.target.value)}
                              placeholder="Alt Menü Adı"
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-600"
                            />
                            <input
                              type="text"
                              value={subItem.path}
                              onChange={(e) => updateSubmenuItem(navIndex, subIndex, 'path', e.target.value)}
                              placeholder="/path"
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-600"
                            />
                            <button
                              onClick={() => removeSubmenuItem(navIndex, subIndex)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer Settings */}
        {activeTab === 'footer' && (
          <div className="space-y-6">
            {/* Description */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4">Açıklama</h2>
              <textarea
                value={footerSettings.description}
                onChange={(e) => setFooterSettings({ ...footerSettings, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
              />
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4">İletişim Bilgileri</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Adres</label>
                  <input
                    type="text"
                    value={footerSettings.contact.address}
                    onChange={(e) => setFooterSettings({
                      ...footerSettings,
                      contact: { ...footerSettings.contact, address: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Telefon</label>
                    <input
                      type="text"
                      value={footerSettings.contact.phone}
                      onChange={(e) => setFooterSettings({
                        ...footerSettings,
                        contact: { ...footerSettings.contact, phone: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">E-posta</label>
                    <input
                      type="email"
                      value={footerSettings.contact.email}
                      onChange={(e) => setFooterSettings({
                        ...footerSettings,
                        contact: { ...footerSettings.contact, email: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Hızlı Linkler</h2>
                <button
                  onClick={() => addFooterLink('quick')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
                >
                  <Plus size={16} />
                  Yeni Link Ekle
                </button>
              </div>
              <div className="space-y-3">
                {footerSettings.quickLinks.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-red-300 transition-colors">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Link Başlığı
                        </label>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateFooterLink('quick', index, 'name', e.target.value)}
                          placeholder="örn: Hakkımızda"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Link Yolu
                        </label>
                        <input
                          type="text"
                          value={item.path}
                          onChange={(e) => updateFooterLink('quick', index, 'path', e.target.value)}
                          placeholder="örn: /about"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.enabled}
                          onChange={(e) => updateFooterLink('quick', index, 'enabled', e.target.checked)}
                          className="w-4 h-4 accent-red-600"
                        />
                        <span className="text-sm text-gray-600">Aktif</span>
                      </label>
                      <button
                        onClick={() => removeFooterLink('quick', index)}
                        className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                      >
                        <Trash2 size={16} />
                        Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Categories Links */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Kategori Linkleri</h2>
                <button
                  onClick={() => addFooterLink('category')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
                >
                  <Plus size={16} />
                  Yeni Kategori Ekle
                </button>
              </div>
              <div className="space-y-3">
                {footerSettings?.categories?.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-red-300 transition-colors">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Kategori Adı
                        </label>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateFooterLink('category', index, 'name', e.target.value)}
                          placeholder="örn: Kulaklıklar"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Kategori Yolu
                        </label>
                        <input
                          type="text"
                          value={item.path}
                          onChange={(e) => updateFooterLink('category', index, 'path', e.target.value)}
                          placeholder="örn: /category/headphones"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.enabled}
                          onChange={(e) => updateFooterLink('category', index, 'enabled', e.target.checked)}
                          className="w-4 h-4 accent-red-600"
                        />
                        <span className="text-sm text-gray-600">Aktif</span>
                      </label>
                      <button
                        onClick={() => removeFooterLink('category', index)}
                        className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                      >
                        <Trash2 size={16} />
                        Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Media - Advanced */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4">Sosyal Medya Hesapları</h2>
              <p className="text-sm text-gray-600 mb-4">Hangi sosyal medya platformlarının gösterileceğini seçin ve URL'lerini girin</p>
              <div className="space-y-4">
                {['facebook', 'instagram', 'twitter', 'youtube'].map((platform) => (
                  <div key={platform} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      checked={footerSettings.socialMedia[platform]?.enabled || false}
                      onChange={(e) => setFooterSettings({
                        ...footerSettings,
                        socialMedia: {
                          ...footerSettings.socialMedia,
                          [platform]: {
                            ...footerSettings.socialMedia[platform],
                            enabled: e.target.checked
                          }
                        }
                      })}
                      className="w-5 h-5 accent-red-600"
                    />
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-2 capitalize">{platform}</label>
                      <input
                        type="url"
                        value={footerSettings.socialMedia[platform]?.url || ''}
                        onChange={(e) => setFooterSettings({
                          ...footerSettings,
                          socialMedia: {
                            ...footerSettings.socialMedia,
                            [platform]: {
                              ...footerSettings.socialMedia[platform],
                              url: e.target.value
                            }
                          }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 disabled:bg-gray-100"
                        placeholder={`https://${platform}.com/...`}
                        disabled={!footerSettings.socialMedia[platform]?.enabled}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Sections */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">Özel Footer Bölümleri</h2>
                  <p className="text-sm text-gray-600 mt-1">Footer'a dinamik bölümler ekleyin (örn: Yardım, Destek, vb.)</p>
                </div>
                <button
                  onClick={addCustomSection}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-semibold"
                >
                  <Plus size={16} />
                  Yeni Bölüm Ekle
                </button>
              </div>
              
              {footerSettings.customSections && footerSettings.customSections.length > 0 ? (
                <div className="space-y-6">
                  {footerSettings?.customSections?.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-3 mb-4">
                        <input
                          type="checkbox"
                          checked={section.enabled}
                          onChange={(e) => updateCustomSection(sectionIndex, 'enabled', e.target.checked)}
                          className="w-4 h-4 accent-purple-600"
                        />
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => updateCustomSection(sectionIndex, 'title', e.target.value)}
                          placeholder="Bölüm Başlığı"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 font-semibold"
                        />
                        <button
                          onClick={() => addCustomSectionLink(sectionIndex)}
                          className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold"
                        >
                          <Plus size={14} />
                          Link Ekle
                        </button>
                        <button
                          onClick={() => removeCustomSection(sectionIndex)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      {/* Section Links */}
                      <div className="ml-8 space-y-2">
                        {section.links && section.links.length > 0 ? (
                          section.links.map((link, linkIndex) => (
                            <div key={linkIndex} className="flex items-center gap-2 p-2 bg-white rounded">
                              <input
                                type="checkbox"
                                checked={link.enabled}
                                onChange={(e) => updateCustomSectionLink(sectionIndex, linkIndex, 'enabled', e.target.checked)}
                                className="w-3 h-3 accent-purple-600"
                              />
                              <input
                                type="text"
                                value={link.name}
                                onChange={(e) => updateCustomSectionLink(sectionIndex, linkIndex, 'name', e.target.value)}
                                placeholder="Link Adı"
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-600"
                              />
                              <input
                                type="text"
                                value={link.path}
                                onChange={(e) => updateCustomSectionLink(sectionIndex, linkIndex, 'path', e.target.value)}
                                placeholder="/path"
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-600"
                              />
                              <button
                                onClick={() => removeCustomSectionLink(sectionIndex, linkIndex)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">Bu bölümde henüz link yok</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Henüz özel bölüm eklenmemiş. "Yeni Bölüm Ekle" butonuna tıklayarak başlayın.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSiteSettings;
